import {useEffect, useState} from "react";
import {Alert, Card, Spinner} from "react-bootstrap";
import {Link, useSearchParams} from "react-router";
import {CheckoutApi} from "../../api/CheckoutApi";
import {OrderViewModel} from "../../api/models";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {CopyButton} from "../../components/CopyButton";
import {formatPrice} from "../../components/format";
import {announce} from "../../components/announce";
import {TrademarkNoticesFor} from "../../components/TrademarkNotices";
import {checkout as messages, format, rich} from "../../i18n";

// How long to wait for a payment that's clearing before saying it'll be emailed: checks every few seconds, a few times.
export const PENDING_CHECKS = 5;
export const PENDING_CHECK_MS = 3000;

// Where Stripe, PayPal and Paddle send the customer back (?order=, and &key= for a guest's order: only that key lets a
// signed-out browser see it). Completing takes the payment (PayPal) or checks it
// (Stripe, Paddle), and issues the license; the provider's webhook does the same if the customer never gets here.
export function CheckoutCompletePage() {
    const [search] = useSearchParams();
    const orderId = search.get('order') ?? '';
    const key = search.get('key') ?? undefined;
    const [order, setOrder] = useState<OrderViewModel>();
    const [error, setError] = useState<unknown>();
    const [checks, setChecks] = useState(0);

    useEffect(() => {
        if (!orderId) {
            return;
        }
        let current = true;
        const timer = setTimeout(() => {
            CheckoutApi.complete(orderId, key).then(
                result => {
                    if (!current) {
                        return;
                    }
                    setOrder(result);
                    if (result.status === 'fulfilled') {
                        announce(messages.complete.announceReady);
                    } else if (result.status === 'pending' && checks + 1 < PENDING_CHECKS) {
                        setChecks(checks + 1);
                    }
                },
                err => { if (current) setError(err); });
        }, checks === 0 ? 0 : PENDING_CHECK_MS);
        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [orderId, checks]);

    if (!orderId) {
        return <><PageHeader title={messages.complete.checkout}/><Alert variant="secondary">{rich(messages.complete.noOrder, { link: text => <Link to="/account/orders">{text}</Link> })}</Alert></>;
    }
    if (error) {
        return <><PageHeader title={messages.complete.checkout}/><ErrorAlert error={error} messages={{ 404: messages.complete.orderNotFound }}/></>;
    }
    if (!order) {
        return <><PageHeader title={messages.complete.finishing}/><LoadingSpinner/></>;
    }

    const retry = (text: string) => <Link to={`/checkout/${encodeURIComponent(order.productSku)}`}>{text}</Link>;
    switch (order.status) {
        case 'fulfilled':
        case 'refunded':
            return (
                <>
                    <PageHeader title={messages.complete.thankYou}/>
                    <p>{format(messages.complete.ready, { product: order.productName })}</p>
                    {order.accountEmail && (
                        <Alert variant="info">
                            {order.accountCreated
                                ? format(messages.complete.accountCreated, { email: order.accountEmail })
                                : rich(messages.complete.accountExisting, { email: text => <strong>{text}</strong> }, { email: order.accountEmail })}
                        </Alert>
                    )}
                    {order.licenseKey && (
                        <Card className="mb-4">
                            <Card.Body className="d-flex flex-wrap align-items-center gap-2">
                                <span className="text-body-secondary">{messages.complete.licenseKey}</span>
                                <span className="license-key fs-5">{order.licenseKey}</span>
                                <CopyButton text={order.licenseKey} label={messages.complete.copyLicenseKey}/>
                            </Card.Body>
                        </Card>
                    )}
                    <OrderSummary order={order}/>
                    <div className="d-flex flex-wrap gap-2">
                        <Link to={`/products/${encodeURIComponent(order.productSku)}/download`} className="btn btn-primary">
                            <i className="bi bi-download me-1" aria-hidden="true"></i>{format(messages.complete.download, { product: order.productName })}
                        </Link>
                        <Link to="/account/licenses" className="btn btn-outline-secondary">{messages.complete.myLicenses}</Link>
                    </div>
                    <TrademarkNoticesFor skus={[order.productSku]} className="mt-5"/>
                </>
            );
        case 'pending':
            return (
                <>
                    <PageHeader title={messages.complete.processing}/>
                    {checks + 1 < PENDING_CHECKS ? (
                        <p><Spinner size="sm" className="me-2" aria-hidden="true"/>{order.provider === 'paypal' ? messages.complete.waitingPayPal : messages.complete.waitingPayment}</p>
                    ) : (
                        <Alert variant="info">
                            {rich(messages.complete.stillProcessing, { link: text => <Link to="/account/licenses">{text}</Link> })}
                        </Alert>
                    )}
                    <OrderSummary order={order}/>
                    <TrademarkNoticesFor skus={[order.productSku]} className="mt-5"/>
                </>
            );
        case 'failed':
            return (
                <>
                    <PageHeader title={messages.complete.declined}/>
                    <Alert variant="danger">{rich(messages.complete.declinedText, { link: retry })}</Alert>
                </>
            );
        default:
            return (
                <>
                    <PageHeader title={messages.complete.expired}/>
                    <Alert variant="secondary">{rich(messages.complete.expiredText, { link: retry })}</Alert>
                </>
            );
    }
}

function OrderSummary({order}: { order: OrderViewModel }) {
    const s = messages.complete.summary;
    return (
        <dl className="row mb-4">
            <dt className="col-sm-3 fw-normal text-body-secondary">{s.order}</dt><dd className="col-sm-9">{order.id}</dd>
            <dt className="col-sm-3 fw-normal text-body-secondary">{s.product}</dt><dd className="col-sm-9">{format(s.productValue, { product: order.productName, edition: order.edition })}</dd>
            <dt className="col-sm-3 fw-normal text-body-secondary">{s.licensee}</dt><dd className="col-sm-9">{order.licensee}</dd>
            <dt className="col-sm-3 fw-normal text-body-secondary">{s.total}</dt>
            <dd className="col-sm-9">{formatPrice(order.total, order.currency)}{order.tax > 0 && <span className="text-body-secondary">{format(s.includingTax, { tax: formatPrice(order.tax, order.currency) })}</span>}</dd>
        </dl>
    );
}
